import {
  RobotoDomainException,
  type RobotoServiceErrorBody,
} from "./exceptions";

/**
 * Shadows `roboto.http.PaginatedList`
 */
export interface PaginatedResponse<T> {
  items: T[];
  next_token: string | null;
  total_count: number | null;
}

/**
 * One element of a batch response. Shadows `roboto.http.BatchResponseElement`.
 *
 * Every element of a batch is applied or refused on its own, so an element carries `data` when the
 * server applied it and `error` when the server refused it, never both. An element carrying neither
 * answers an operation that returns no content.
 */
export interface BatchResponseElement<T> {
  data?: T | null;
  error?: { error: RobotoServiceErrorBody } | null;
}

/**
 * The response to a batch request. Shadows `roboto.http.BatchResponse`.
 *
 * One element per request element, in the order the request sent them, so an element's position in
 * `responses` identifies the request element it answers.
 */
export interface BatchResponse<T> {
  responses: BatchResponseElement<T>[];
}

/**
 * The result the server returned for each element it applied, in request order. Mirrors
 * `roboto.http.BatchResponse.succeeded`.
 */
export function batchSucceeded<T>(response: BatchResponse<T>): T[] {
  return response.responses.flatMap((element) =>
    element.data !== undefined && element.data !== null ? [element.data] : [],
  );
}

/**
 * The exception the server reported for each element it refused, in request order. Mirrors
 * `roboto.http.BatchResponse.failed`. An `error_code` matching no class in `exceptions.ts` yields a
 * base `RobotoDomainException` carrying that code and message, so a failure reported under a code
 * this build does not define still reaches the caller.
 *
 * Any `error` that is present and not `null` is a refusal, whatever its shape. An `error` that is not
 * a refusal envelope (see {@link isRefusalEnvelope}) also yields a base `RobotoDomainException`,
 * carrying the `message` it sent, or its own text as JSON when it sent none. Its `isClientError()` is
 * false, so `userFacingErrorMessage` shows its fallback copy rather than that text.
 */
export function batchFailed<T>(
  response: BatchResponse<T>,
): RobotoDomainException[] {
  return response.responses.flatMap((element) =>
    element.error !== undefined && element.error !== null
      ? [refusalException(element.error)]
      : [],
  );
}

/**
 * Whether `value` is the envelope the server sends for a refused batch element: an object whose
 * `error` carries a string `error_code` and a string `message`.
 */
export function isRefusalEnvelope(
  value: unknown,
): value is { error: RobotoServiceErrorBody } {
  return (
    isRecord(value) &&
    isRecord(value.error) &&
    typeof value.error.error_code === "string" &&
    typeof value.error.message === "string"
  );
}

function refusalException(refusal: unknown): RobotoDomainException {
  if (isRefusalEnvelope(refusal)) {
    return RobotoDomainException.fromHttpResponse(refusal.error);
  }
  const sentMessage =
    isRecord(refusal) && isRecord(refusal.error)
      ? refusal.error.message
      : undefined;
  return new RobotoDomainException({
    // Names no subclass and no HTTP status, so `isClientError()` is false.
    error_code: "RobotoDomainException",
    message:
      typeof sentMessage === "string" ? sentMessage : refusalText(refusal),
  });
}

function refusalText(refusal: unknown): string {
  try {
    return JSON.stringify(refusal) ?? String(refusal);
  } catch {
    // JSON.stringify throws on a bigint or a circular reference.
    return String(refusal);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
